import { Input } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { CompanyFilter } from '../../CompanyFilter.tsx';
import { dayOrNull, type DayRange } from '../../report-period.ts';
import { usePageContext } from '../../usePageContext.ts';
import type { Comparison, ProductFilter } from '../performance.ts';
import { OriginFilter } from '../filters/OriginFilter.tsx';
import { ProductFilter as ProductField } from '../filters/ProductFilter.tsx';
import { ChoiceField } from './ChoiceField.tsx';
import { COMPARISONS } from './comparisons.ts';

export type ChosenDays = { readonly from: string | null; readonly to: string | null };

export type DashboardFiltersProps = {
  api: TypedClient;
  company: string | null;
  productFilter: ProductFilter;
  days: ChosenDays;
  range: DayRange;
  comparison: Comparison;
  onCompanyChange: (company: string | null) => void;
  onProductFilterChange: (filter: ProductFilter) => void;
  onDaysChange: (days: ChosenDays) => void;
  onComparisonChange: (comparison: Comparison) => void;
};

export const DashboardFilters = ({
  api,
  company,
  productFilter,
  days,
  range,
  comparison,
  onCompanyChange,
  onProductFilterChange,
  onDaysChange,
  onComparisonChange,
}: DashboardFiltersProps) => {
  const { t } = usePageContext();
  return (
    <>
      <CompanyFilter api={api} company={company} onCompanyChange={onCompanyChange} />
      <ProductField
        api={api}
        productId={productFilter.productId}
        onProductChange={(productId) => {
          onProductFilterChange({ ...productFilter, productId });
        }}
      />
      <OriginFilter
        api={api}
        origin={productFilter.origin}
        onOriginChange={(origin) => {
          onProductFilterChange({ ...productFilter, origin });
        }}
      />
      <Input
        type="date"
        label={t('dashboard.overview.from')}
        value={range.fromDay}
        max={range.toDay}
        onChange={(event) => {
          onDaysChange({ ...days, from: dayOrNull(event.target.value) });
        }}
      />
      <Input
        type="date"
        label={t('dashboard.overview.to')}
        value={range.toDay}
        min={range.fromDay}
        onChange={(event) => {
          onDaysChange({ ...days, to: dayOrNull(event.target.value) });
        }}
      />
      <ChoiceField
        label={t('dashboard.widgets.compareLabel')}
        value={comparison}
        choices={COMPARISONS}
        labelOf={(choice) => t(`dashboard.widgets.compare.${choice}`)}
        onChange={onComparisonChange}
      />
    </>
  );
};
